import { calendarByOwner, useUsersCalendarList } from 'djangogen/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type EnsurableOwnerType = 'socialgroup' | 'company';

export interface OwnerCalendarTarget {
    ownerId: string | null;
    ownerType: EnsurableOwnerType;
}

interface OwnerCalendarRecord {
    owner_id: string | number | null;
    owner_type: string;
}

interface NormalizedOwnerCalendarTarget {
    ownerId: string;
    ownerType: EnsurableOwnerType;
}

interface UseEnsureOwnerCalendarsOptions {
    enabled?: boolean;
    revalidateOnFocus?: boolean;
    targets?: readonly OwnerCalendarTarget[];
}

function getOwnerCalendarKey(ownerType: EnsurableOwnerType, ownerId: string): string {
    return `${ownerType}:${ownerId}`;
}

function hasOwnerCalendar(
    calendars: readonly OwnerCalendarRecord[] | undefined,
    target: Readonly<NormalizedOwnerCalendarTarget>,
): boolean {
    if (!calendars) {
        return false;
    }

    return calendars.some(
        (calendar) =>
            calendar.owner_type === target.ownerType && String(calendar.owner_id ?? '') === String(target.ownerId),
    );
}

function getOwnerCalendarApiType(ownerType: EnsurableOwnerType): 'group' | 'company' {
    if (ownerType === 'socialgroup') {
        return 'group';
    }

    return 'company';
}

export function useEnsureOwnerCalendars(options: Readonly<UseEnsureOwnerCalendarsOptions> = {}) {
    const { enabled = true, revalidateOnFocus = true, targets = [] } = options;
    const [attemptedOwnerKeys, setAttemptedOwnerKeys] = useState<Record<string, boolean>>({});
    const [ensuringOwnerKeys, setEnsuringOwnerKeys] = useState<Record<string, boolean>>({});
    const inFlightEnsuresRef = useRef<Map<string, Promise<void>>>(new Map());

    const { data, mutate } = useUsersCalendarList({
        swr: {
            revalidateOnFocus,
        },
    });

    const normalizedTargets = useMemo(() => {
        const uniqueTargets = new Map<string, NormalizedOwnerCalendarTarget>();

        for (const target of targets) {
            if (!target.ownerId) {
                continue;
            }

            const normalizedTarget: NormalizedOwnerCalendarTarget = {
                ownerId: String(target.ownerId),
                ownerType: target.ownerType,
            };

            uniqueTargets.set(
                getOwnerCalendarKey(normalizedTarget.ownerType, normalizedTarget.ownerId),
                normalizedTarget,
            );
        }

        return Array.from(uniqueTargets.values());
    }, [targets]);

    const ensureCalendarByOwner = useCallback(
        async (ownerType: EnsurableOwnerType, ownerId: string) => {
            const ownerKey = getOwnerCalendarKey(ownerType, ownerId);
            const existingPromise = inFlightEnsuresRef.current.get(ownerKey);

            if (existingPromise) {
                await existingPromise;
                return;
            }

            if (hasOwnerCalendar(data, { ownerId, ownerType })) {
                return;
            }

            setAttemptedOwnerKeys((currentOwnerKeys) => {
                if (currentOwnerKeys[ownerKey]) {
                    return currentOwnerKeys;
                }

                return {
                    ...currentOwnerKeys,
                    [ownerKey]: true,
                };
            });
            setEnsuringOwnerKeys((currentOwnerKeys) => {
                if (currentOwnerKeys[ownerKey]) {
                    return currentOwnerKeys;
                }

                return {
                    ...currentOwnerKeys,
                    [ownerKey]: true,
                };
            });

            const ensurePromise = (async () => {
                try {
                    await calendarByOwner({
                        id: ownerId,
                        type: getOwnerCalendarApiType(ownerType),
                    });
                    await mutate();
                } catch (error) {
                    console.error('Failed to fetch or create owner calendar', error);
                } finally {
                    inFlightEnsuresRef.current.delete(ownerKey);
                    setEnsuringOwnerKeys((currentOwnerKeys) => {
                        if (!currentOwnerKeys[ownerKey]) {
                            return currentOwnerKeys;
                        }

                        const { [ownerKey]: _removedOwnerKey, ...nextOwnerKeys } = currentOwnerKeys;
                        return nextOwnerKeys;
                    });
                }
            })();

            inFlightEnsuresRef.current.set(ownerKey, ensurePromise);
            await ensurePromise;
        },
        [data, mutate],
    );

    useEffect(() => {
        if (!enabled || data === undefined) {
            return;
        }

        for (const target of normalizedTargets) {
            const ownerKey = getOwnerCalendarKey(target.ownerType, target.ownerId);

            if (hasOwnerCalendar(data, target)) {
                continue;
            }

            if (attemptedOwnerKeys[ownerKey] || ensuringOwnerKeys[ownerKey]) {
                continue;
            }

            void ensureCalendarByOwner(target.ownerType, target.ownerId);
        }
    }, [attemptedOwnerKeys, data, enabled, ensureCalendarByOwner, ensuringOwnerKeys, normalizedTargets]);

    const hasAttemptedOwnerCalendar = useCallback(
        (ownerType: EnsurableOwnerType, ownerId: string | null) => {
            if (!ownerId) {
                return false;
            }

            return Boolean(attemptedOwnerKeys[getOwnerCalendarKey(ownerType, ownerId)]);
        },
        [attemptedOwnerKeys],
    );

    const isEnsuringOwnerCalendar = useCallback(
        (ownerType: EnsurableOwnerType, ownerId: string | null) => {
            if (!ownerId) {
                return false;
            }

            return Boolean(ensuringOwnerKeys[getOwnerCalendarKey(ownerType, ownerId)]);
        },
        [ensuringOwnerKeys],
    );

    return {
        data,
        ensureCalendarByOwner,
        hasAttemptedOwnerCalendar,
        hasLoadedCalendars: data !== undefined,
        isEnsuringOwnerCalendar,
    };
}
